<template>
   <Skeleton :loading="loading" height="100%">
      <form class="flex flex-col gap-8" @submit.prevent="handleSubmit">
         <div class="__form-col">
            <slot />
         </div>
         <div v-if="$slots.footer">
            <slot name="footer" />
         </div>
      </form>
   </Skeleton>
</template>

<script lang="ts" setup>
import { FORM_DATA_SYNC } from '../../composables/form-data'
import { useNotification } from '../../composables/notification'
import { isEmptyValue } from '../../utils/form-validator'
import type { TextInputType } from './TextInput.vue'

import type { AvailableLocale, SelectOption, Translation } from '@xleddyl/types'
import { ref, watch } from 'vue'

type Props = {
   loading?: boolean
}
type Emits = {
   submit: [any]
   dirty: [boolean]
}
export type FormFields = {
   [key: string]: {
      placeholder?: string | number | null
      label?: string | number | null
      required?: boolean
      error?: string
      disabled?: boolean
      min?: number
      max?: number
   } & (
      | {
           value?: string | number | null
           type: TextInputType
           maxLength?: number
           /** returns a translation key */
           validator?: (v?: string | null, form?: FormFields) => string | undefined
        }
      | {
           value?: boolean | null
           type: 'checkbox'
           /** returns a translation key */
           validator?: (v?: boolean | null, form?: FormFields) => string | undefined
        }
      | {
           value?: string[] | null
           type: 'checkbox-group'
           /** returns a translation key */
           validator?: (v?: string[] | null, form?: FormFields) => string | undefined
        }
      | {
           value?: Translation | null
           type: 'translation'
           locales: AvailableLocale[]
           maxLength?: number
           mode?: 'text' | 'textarea' | 'rich-text'
           /** returns a translation key */
           validator?: (v?: Translation | null, form?: FormFields) => string | undefined
        }
      | {
           value?: string | number | null
           type: 'select'
           options?: SelectOption[]
           /** returns a translation key */
           validator?: (v?: string | number | null, form?: FormFields) => string | undefined
        }
      | {
           value?: string | null
           type: 'date'
           mode?: 'date' | 'calendar' | 'specific' | 'html-date'
           /** returns a translation key */
           validator?: (v?: string | null, form?: FormFields) => string | undefined
        }
      | {
           value?: any[] | null
           type: 'array'
           single?: boolean
           /** returns a translation key */
           validator?: (v?: any[], form?: FormFields) => string | undefined
        }
   )
}

const props = withDefaults(defineProps<Props>(), {})
const model = defineModel<FormFields>({ required: true })
const emit = defineEmits<Emits>()
const notification = useNotification()

const { t } = useI18n()

const state = ref<'valid' | 'error'>('valid')

let initialValues: Record<string, unknown> = {}
let initialSnapshot = ''
let lastSyncToken: number | undefined
let isInitialized = false

const cloneValue = (value: unknown): unknown => {
   if (value === null || typeof value !== 'object') return value
   if (value instanceof File || value instanceof Date) return value
   if (Array.isArray(value)) return value.map(cloneValue)
   return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, v]) => [key, cloneValue(v)])
   )
}

const serializeValue = (value: unknown): unknown => {
   if (value === null || typeof value !== 'object') return value
   if (value instanceof File) return `${value.name}:${value.size}:${value.lastModified}`
   if (value instanceof Date) return value.toISOString()
   if (Array.isArray(value)) return value.map(serializeValue)
   return Object.entries(value as Record<string, unknown>)
      .sort(([a], [b]) => (a < b ? -1 : 1))
      .map(([key, v]) => [key, serializeValue(v)])
}

const snapshotValues = (form?: FormFields) =>
   JSON.stringify(
      Object.keys(form || {})
         .sort()
         .map((key) => [key, serializeValue(form?.[key]?.value)])
   )

const readSyncToken = (form?: FormFields) =>
   (form as unknown as Record<symbol, number | undefined> | undefined)?.[FORM_DATA_SYNC]

const captureBaseline = (form?: FormFields) => {
   initialValues = Object.fromEntries(
      Object.entries(form || {}).map(([key, field]) => [key, cloneValue(field?.value)])
   )
   initialSnapshot = snapshotValues(form)
   lastSyncToken = readSyncToken(form)
}

const isFieldEmpty = (field: any) =>
   field.type === 'translation'
      ? Object.values(field.value || {}).every((v) => isEmptyValue(v))
      : isEmptyValue(field.value)

const validateField = (field: any, formData: FormFields): string | undefined => {
   if (field.required && isFieldEmpty(field)) {
      return t('form.error.field-required')
   }

   if (typeof field.maxLength === 'number') {
      const values = field.type === 'translation' ? Object.values(field.value || {}) : [field.value]
      if (values.some((v) => typeof v === 'string' && v.length > field.maxLength)) {
         return t('form.error.max-length', { max: field.maxLength })
      }
   }

   if (typeof field.value === 'number') {
      if (typeof field.min === 'number' && field.value < field.min) {
         return t('form.error.min-value', { min: field.min })
      }
      if (typeof field.max === 'number' && field.value > field.max) {
         return t('form.error.max-value', { max: field.max })
      }
   } else if (Array.isArray(field.value)) {
      if (typeof field.min === 'number' && field.value.length < field.min) {
         return t('form.error.min-value', { min: field.min })
      }
      if (typeof field.max === 'number' && field.value.length > field.max) {
         return t('form.error.max-value', { max: field.max })
      }
   }

   const error = field.validator?.(field.value as never, formData)
   if (error) {
      return t(error)
   }

   return undefined
}

const handleSubmit = () => {
   state.value = 'valid'
   if (!model.value) return

   Object.values(model.value).forEach((v) => {
      v.error = undefined
      const error = validateField(v, model.value)
      if (error) {
         state.value = 'error'
         v.error = error
      }
   })

   if (state.value === 'valid') {
      emit('submit', model.value)
   } else {
      notification.add({
         type: 'error',
         title: t('error.generic'),
         description: Object.values(model.value)
            .filter((v) => !!v.error && v.error !== ' ')
            .map((v) => `${v.label}: ${v.error}`),
      })
   }
}

const handleReset = (hard?: boolean) => {
   if (!model.value) return

   Object.keys(model.value).forEach((key) => {
      const field = model.value[key]
      if (!field) return
      field.value = (hard ? undefined : cloneValue(initialValues[key])) as never
      field.error = undefined
   })

   emit('dirty', snapshotValues(model.value) !== initialSnapshot)
}

watch(
   model,
   (newVal) => {
      const syncToken = readSyncToken(newVal)

      if (props.loading || !isInitialized || syncToken !== lastSyncToken) {
         captureBaseline(newVal)
         isInitialized = true
         emit('dirty', false)
         return
      }

      emit('dirty', snapshotValues(newVal) !== initialSnapshot)
   },
   { immediate: true, deep: true }
)

watch(
   () => props.loading,
   (loading) => {
      if (!loading && model.value) {
         captureBaseline(model.value)
         emit('dirty', false)
      }
   }
)

defineExpose({ submit: handleSubmit, reset: handleReset })
</script>
